import { captureException } from '@sentry/react'
import { inE2E } from 'config/environment'
import { useQuery } from '@tanstack/react-query'
import { api, query, queryClient, type ApiResponse } from 'config/api'
import { useFeatureFlags } from 'hooks/featureFlags'
import { useGradeOverrides } from 'hooks/useGradeOverrides'
import { useBreakoutUser } from 'hooks/profile'
import type { useMeetingResultsCubit } from 'hooks/cubits/meetingsResults'
import { useEffect } from 'react'
import { getGradeOverride, isAppealOverride } from 'util/gradeOverrides'
import type { ScoreComponentKey } from './scoreComponents'
import { toFinalScorePercent } from '@breakoutlearning/firebase-repository/sources-of-truth/percentage'

type MeetingResultsCubit = ReturnType<typeof useMeetingResultsCubit>

const MAX_TIMEOUT_DURATION_MS = 2_147_483_647

type AppealComponentInput = {
  proposedScore: number
  justification: string
}

export type AppealComponents = Partial<
  Record<ScoreComponentKey, AppealComponentInput>
>

type ShowResponse = ApiResponse<'api.v1.assignments.rooms.grade_appeal.show'>

export type GradeAppealDto = NonNullable<ShowResponse['appeal']>

export type GradeAppealActionBlockedReason = 'not_graded' | 'window_closed'

function appealIds(cubit: MeetingResultsCubit) {
  return {
    assignmentId: cubit.meetingCubit.assignmentId ?? '',
    roomId: cubit.roomStateId ?? '',
  }
}

/**
 * Returns the effective override for the viewed result. Students also receive
 * their appeal state and eligibility; faculty only receive the override.
 *
 * `finalScoreData` covers assignment-loaded and AI-grading-complete. Appeal
 * eligibility also checks both assignment switches explicitly.
 */
export function useGradeAppeal(cubit: MeetingResultsCubit) {
  const { studentGradeAppealsEnabled } = useFeatureFlags()
  const viewer = useBreakoutUser()

  const { assignmentId, roomId } = appealIds(cubit)
  const sectionId = cubit.meetingCubit.roomState.data.sectionId ?? ''

  const hasGradeContext = !!assignmentId && !!roomId && !!cubit.finalScoreData
  const isViewingOwnResult = viewer.user.id === cubit.userId
  const canReadStudentAppeal = hasGradeContext && isViewingOwnResult
  const canReadInstructorOverride =
    hasGradeContext &&
    !!sectionId &&
    !isViewingOwnResult &&
    (viewer.isFaculty || viewer.isCorre)

  const result = useQuery({
    ...query.api.v1.assignments.rooms.gradeAppeal.show.queryOptions({
      params: { assignmentId, roomId },
    }),
    enabled: canReadStudentAppeal,
    staleTime: 30_000,
  })

  const instructorOverrides = useGradeOverrides(
    sectionId,
    assignmentId,
    canReadInstructorOverride
  )

  const responseError =
    result.error ??
    (result.data && 'error' in result.data ? result.data.error : null)

  useEffect(() => {
    // E2E leaves Sentry uninitialized; its v7/v8 hubs collide on capture.
    if (!responseError || inE2E()) return

    captureException(responseError, {
      tags: { feature: 'grade-appeal' },
      extra: { assignmentId, roomId },
    })
  }, [assignmentId, responseError, roomId])

  const state = result.data && !('error' in result.data) ? result.data : null
  const refetchAppealState = result.refetch
  const appealWindowRemainingMs =
    state?.canFile === true ? (state.appealWindow?.remainingMs ?? null) : null

  useEffect(() => {
    if (appealWindowRemainingMs === null) return

    const timeoutId = window.setTimeout(
      () => void refetchAppealState(),
      Math.min(appealWindowRemainingMs, MAX_TIMEOUT_DURATION_MS)
    )

    return () => window.clearTimeout(timeoutId)
  }, [appealWindowRemainingMs, refetchAppealState])

  const appeal: GradeAppealDto | null = state?.appeal ?? null
  const filingConfigured =
    canReadStudentAppeal &&
    studentGradeAppealsEnabled &&
    cubit.assignment.data.gradeAppealsEnabled === true &&
    cubit.assignment.data.showFinalGradeToParticipants === true
  const actionBlockedReason: GradeAppealActionBlockedReason | null =
    state?.blockedReason === 'result_not_found'
      ? 'not_graded'
      : state?.blockedReason === 'window_closed'
        ? 'window_closed'
        : null
  const studentOverrideTotalScore = state?.overrideTotalScore ?? null
  const instructorOverrideTotalScore = getGradeOverride(
    instructorOverrides.overrideMap,
    assignmentId,
    cubit.userId,
    roomId
  )
  const studentAppealOverride =
    studentOverrideTotalScore !== null &&
    (appeal?.status === 'accepted' || appeal?.status === 'partially-accepted')
  const instructorAppealOverride =
    canReadInstructorOverride &&
    isAppealOverride({
      overrides: instructorOverrides.overrideMap,
      assignmentId,
      userId: cubit.userId,
      roomId,
    })
  const overrideTotalScore = canReadInstructorOverride
    ? (instructorOverrideTotalScore ?? null)
    : studentOverrideTotalScore
  const isPending = canReadInstructorOverride
    ? instructorOverrides.isPending
    : canReadStudentAppeal && result.isPending
  const isError = canReadStudentAppeal && responseError !== null
  const canShowAction =
    filingConfigured &&
    !isPending &&
    !isError &&
    appeal === null &&
    (state?.canFile === true || actionBlockedReason !== null)
  const canSubmitDraft =
    filingConfigured && state?.canFile === true && actionBlockedReason === null

  return {
    isPending,
    isError,
    canShowAction,
    actionBlockedReason,
    canFile:
      canShowAction && actionBlockedReason === null && state?.canFile === true,
    canSubmitDraft,
    appeal: canReadStudentAppeal ? appeal : null,
    overrideFinalScore:
      overrideTotalScore === null
        ? null
        : toFinalScorePercent(overrideTotalScore),
    appealOverride: canReadInstructorOverride
      ? instructorAppealOverride
      : canReadStudentAppeal && studentAppealOverride,
    currentScores: canReadStudentAppeal ? (state?.currentScores ?? null) : null,
    appealWindowClosesAt: canReadStudentAppeal
      ? (state?.appealWindow?.closesAt ?? null)
      : null,
    refetch: canReadInstructorOverride
      ? instructorOverrides.refetch
      : refetchAppealState,
  }
}

export async function submitGradeAppeal(
  cubit: MeetingResultsCubit,
  components: AppealComponents
) {
  const result = await api.v1.assignments.rooms.gradeAppeal
    .store({ params: appealIds(cubit), body: { components } })
    .safe()
  const [submitted, error] = result
  if (!error && submitted?.success) {
    cubit.logEvent('grade_appeal_submitted', {
      appeal_id: submitted.appeal.id,
      components: Object.keys(components).join(','),
    })
  }
  return result
}

export function invalidateGradeAppeal(cubit: MeetingResultsCubit) {
  return queryClient.invalidateQueries(
    query.api.v1.assignments.rooms.gradeAppeal.show.queryFilter({
      params: appealIds(cubit),
    })
  )
}
